import dayjs from "dayjs";
import type { TFunction } from "i18next";
import { normalizeBookingStatus, type BookingStatus } from "./bookingStatus";

/** Shape aligned with `BookingRecord` on the Bookings page (subset for CSV). */
export type BookingListCsvRow = {
  id: number;
  reference: string;
  status: string;
  eta?: string | null;
  company_name: string;
  rejection_reason?: string | null;
  origin_pickup_date: string;
  created_at: string;
  transport_type?: {
    name: string;
    abbreviation: string;
  };
  booking_type?: { name: string };
  booking_incoterm?: { name: string };
  pickup_request?: Array<{
    status: string;
    cost?: number;
    created_at: string;
    notes?: string;
  }>;
  container?: {
    reference: string | null;
    eta: string | null;
    etd: string | null;
    delayed_etd?: string | null;
    eta_customer?: string | null;
    status: string;
    track_and_trace: string | null;
    origin_port?: { name: string; country: string | null };
    destination_port?: { name: string; country: string | null };
  };
  supplier?: { name: string };
  products_summary?: {
    total_cbm: number;
    total_cartons: number;
    total_weight: number;
    total_pieces: number;
    product_count: number;
  };
  products?: Array<{ name: string; ean?: string }>;
  delivery_details?: Array<{
    country: string;
    city: string;
    postal_code: string;
    street_name: string;
    house_number: string;
    instructions: string | null;
  }>;
};

function escapeCsvCell(value: string): string {
  const needsQuotes =
    value.includes(",") ||
    value.includes('"') ||
    value.includes("\n") ||
    value.includes("\r");
  const escaped = value.replace(/"/g, '""');
  return needsQuotes ? `"${escaped}"` : escaped;
}

function formatPort(
  port?: { name: string; country: string | null } | null,
): string {
  if (!port?.name) return "";
  return port.country ? `${port.name} (${port.country})` : port.name;
}

function latestPickup(record: BookingListCsvRow) {
  if (!record.pickup_request?.length) return null;
  return [...record.pickup_request].sort(
    (a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  )[0];
}

function pickupStatusLabel(
  status: string,
  t: TFunction,
): string {
  switch (status) {
    case "requested":
      return String(t("pickupRequest.statusRequested", "Requested"));
    case "pending":
      return String(t("pickupRequest.statusPending", "Pending"));
    case "offer":
      return String(t("pickupRequest.statusOffer", "Offer"));
    case "accepted":
      return String(t("pickupRequest.statusAccepted", "Accepted"));
    case "expired":
      return String(t("pickupRequest.statusExpired", "Expired"));
    default:
      return status;
  }
}

function statusLabel(record: BookingListCsvRow, t: TFunction): string {
  const normalized = normalizeBookingStatus(record.status) as BookingStatus;
  const translation = t(`status.${normalized}`);
  return typeof translation === "string" ? translation : normalized;
}

function deliverySummary(record: BookingListCsvRow, t: TFunction): string {
  if (record.delivery_details && record.delivery_details.length > 0) {
    const lines = record.delivery_details.map((d) => {
      const line1 = `${d.street_name} ${d.house_number}, ${d.postal_code} ${d.city}, ${d.country}`;
      return d.instructions ? `${line1} (${d.instructions})` : line1;
    });
    return lines.join("\n");
  }
  return String(t("bookings.inStorage", "In storage"));
}

function productLines(record: BookingListCsvRow): string {
  if (!record.products?.length) return "";
  return record.products
    .map((p) => `${p.name}${p.ean ? ` (${p.ean})` : ""}`)
    .join("\n");
}

function buildRow(record: BookingListCsvRow, t: TFunction): string[] {
  const pickup = latestPickup(record);
  const bookingIdDisplay = `EFF${record.id}${record.transport_type?.abbreviation || ""}`;
  const finalEta = record.eta || record.container?.eta_customer;

  return [
    record.company_name || "",
    bookingIdDisplay,
    record.reference || "",
    record.status || "",
    statusLabel(record, t),
    record.rejection_reason || "",
    record.transport_type?.name || "",
    record.booking_type?.name || "",
    record.booking_incoterm?.name || "",
    pickup ? pickupStatusLabel(pickup.status, t) : "",
    pickup?.cost != null ? String(pickup.cost) : "",
    pickup?.notes || "",
    pickup ? dayjs(pickup.created_at).format("YYYY-MM-DD") : "",
    record.origin_pickup_date
      ? dayjs(record.origin_pickup_date).format("YYYY-MM-DD")
      : "",
    formatPort(record.container?.origin_port),
    formatPort(record.container?.destination_port),
    record.supplier?.name || "",
    dayjs(record.created_at).format("YYYY-MM-DD"),
    record.container?.reference || "",
    record.container?.eta
      ? dayjs(record.container.eta).format("YYYY-MM-DD")
      : "",
    finalEta ? dayjs(finalEta).format("YYYY-MM-DD") : "",
    record.container?.delayed_etd || record.container?.etd
      ? dayjs(record.container.delayed_etd || record.container.etd).format(
          "YYYY-MM-DD"
        )
      : "",
    record.container?.status || "",
    String(record.products_summary?.product_count ?? ""),
    productLines(record),
    String(record.products_summary?.total_pieces ?? ""),
    String(record.products_summary?.total_cartons ?? ""),
    record.products_summary?.total_weight != null
      ? String(record.products_summary.total_weight)
      : "",
    record.products_summary?.total_cbm != null
      ? String(record.products_summary.total_cbm)
      : "",
    record.container?.track_and_trace || "",
    record.delivery_details && record.delivery_details.length > 0
      ? String(t("bookings.shippedToAddress", "Shipped to address"))
      : String(t("bookings.inStorage", "In storage")),
    deliverySummary(record, t),
  ];
}

function buildHeaders(t: TFunction): string[] {
  return [
    String(t("bookings.company", "Company")),
    String(t("bookings.bookingId")),
    String(t("bookings.reference")),
    String(t("bookings.status", "Status")),
    String(t("bookings.statusDisplay", "Status (display)")),
    String(t("bookings.csvRejectionReason", "Rejection reason")),
    String(t("bookings.transportType")),
    String(t("bookings.bookingType")),
    String(t("bookings.incoterm")),
    String(t("bookings.pickupRequestStatus", "Pickup request status")),
    String(t("bookings.pickupRequestCost", "Pickup request cost (EUR)")),
    String(t("bookings.pickupRequestNotes", "Pickup request notes")),
    String(t("bookings.pickupRequestDate", "Pickup request date")),
    String(t("bookings.cargoReadinessDate")),
    String(
      t("bookings.containerOriginPort", "Container origin port"),
    ),
    String(
      t("bookings.containerDestinationPort", "Container destination port"),
    ),
    String(t("bookings.supplier")),
    String(t("bookings.createdAt")),
    String(t("bookings.containerReference")),
    String(t("bookings.containerETA")),
    String(t("bookings.finalETA", "ETA")),
    String(t("bookings.containerETD")),
    String(t("bookings.containerStatusLabel", "Container status")),
    String(t("bookings.totalProducts")),
    String(t("bookings.productsList", "Products (list)")),
    String(t("bookings.totalPieces")),
    String(t("bookings.totalCartons")),
    String(t("bookings.totalWeight")),
    String(t("bookings.totalCBM")),
    String(t("bookings.trackAndTrace")),
    String(t("bookings.deliveryType")),
    String(t("bookings.deliveryDetails", "Delivery details")),
  ];
}

export function buildBookingsListCsv(
  records: BookingListCsvRow[],
  t: TFunction,
): string {
  const headers = buildHeaders(t);
  const lines = [
    headers.map(escapeCsvCell).join(","),
    ...records.map((r) =>
      buildRow(r, t).map((c) => escapeCsvCell(c ?? "")).join(","),
    ),
  ];
  return `\uFEFF${lines.join("\n")}`;
}

export function downloadBookingsListCsv(
  records: BookingListCsvRow[],
  filename: string,
  t: TFunction,
): void {
  const csv = buildBookingsListCsv(records, t);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
}

/** Status filters for CSV export (matches Bookings tabs; `all` = non-archived). */
export const BOOKINGS_CSV_EXPORT_SCOPE_KEYS = [
  "all",
  "concept",
  "submitted",
  "approved",
  "rejected",
  "waiting_for_goods",
  "goods_received_origin",
  "in_transit",
  "customs_pending_clearance",
  "customs_cleared",
  "fyco",
  "delivery_planned",
  "out_for_delivery",
  "delivered",
  "added_to_stock",
  "completed",
  "cancelled",
] as const;
